// SCR-484, Sign-in status and IdP setup (REQ-174, REQ-161, REQ-162): each enabled method with
// its standing and last check, the URLs to register at the IdP, the SAML SP metadata download,
// the OIDC client secret write and the trusted token issuers. The customer admin's screen on the
// proxy only: the secret goes to the proxy account (decision 9) and is never shown, read back or
// kept in the page after the write. served_by is AllCode's to set and is shown read-only. The
// test sign-in on /login is confirmed here, which unblocks the flip (decision 8). Nothing here
// lists IdPs to anyone but the admin (S3).
import {
  certificateNote,
  failureMessage,
  formatWhen,
  TEST_SIGN_IN_PATH,
  type Failure,
  type MethodStatus,
  type SignInSource,
  type SignInStatus,
} from "./source";
import { useEffect, useState } from "preact/hooks";
import { Skeleton } from "./keys";
import { SIGN_IN_EDITOR_ANCHOR } from "../signin/model";

/** The settings share the page (SIG-2): the status links to the editor's first card. */
const EDITOR_HREF = `#${SIGN_IN_EDITOR_ANCHOR}`;

export interface SignInStatusProps {
  source: SignInSource;
  now?: () => Date;
}

type State = { phase: "loading" } | { phase: "failed"; failure: Failure } | { phase: "ready"; status: SignInStatus };

const KIND_LABELS: Readonly<Record<string, string>> = { sso: "Single sign-on", cognito: "Cognito", iam: "AWS IAM" };

export function SignInStatusScreen({ source, now = () => new Date() }: SignInStatusProps) {
  const [state, setState] = useState<State>({ phase: "loading" });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ phase: "loading" });
    void source.status().then((result) => {
      if (live) setState(result.ok ? { phase: "ready", status: result.value } : { phase: "failed", failure: result.failure });
    });
    return () => {
      live = false;
    };
  }, [source, round]);
  const reload = () => setRound((n) => n + 1);

  return (
    <section class="idn" aria-labelledby="idn-signin-title">
      <header class="idn-head">
        <h1 id="idn-signin-title">Sign-in</h1>
        <p class="idn-lede">
          How people sign in on this proxy, and what each identity provider needs from you. Add or change methods in the{" "}
          <a href={EDITOR_HREF}>settings below</a>.
        </p>
      </header>
      {state.phase === "loading" ? <Skeleton /> : null}
      {state.phase === "failed" ? <Failed failure={state.failure} onRetry={reload} /> : null}
      {state.phase === "ready" ? <Ready status={state.status} source={source} now={now()} onReload={reload} /> : null}
    </section>
  );
}

function Failed({ failure, onRetry }: { failure: Failure; onRetry(): void }) {
  if (failure === "forbidden" || failure === "session-ended") {
    return (
      <div class="card idn-note" role="alert">
        <h2>{failure === "forbidden" ? "Not available" : "Your session ended"}</h2>
        <p>{failureMessage(failure, "customer")}</p>
      </div>
    );
  }
  return (
    <div class="banner banner-error" role="alert">
      <p>Couldn't load the sign-in status. {failureMessage(failure, "customer")}</p>
      <button type="button" class="button" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function Ready({ status, source, now, onReload }: { status: SignInStatus; source: SignInSource; now: Date; onReload(): void }) {
  if (!status.loaded) {
    return (
      <div class="card idn-note">
        <h2>Waiting for this proxy's settings</h2>
        <p>This proxy has not received its settings from AllCode yet. Its sign-in methods appear here once it has.</p>
      </div>
    );
  }
  const cert = certificateNote(status.spCertificateNotAfter, now);
  return (
    <>
      <dl class="idn-facts">
        <div>
          <dt>Sign-in served by</dt>
          <dd>
            {status.servedBy === "proxy" ? "This proxy" : "AllCode, centrally"} <span class="idn-sub">(set by AllCode)</span>
          </dd>
        </div>
        <div>
          <dt>Settings applied</dt>
          <dd>{status.appliedAt ? formatWhen(status.appliedAt) : "not yet"}</dd>
        </div>
        <div>
          <dt>Last check</dt>
          <dd>
            {formatWhen(status.checkedAt)}{" "}
            <button type="button" class="button-link" onClick={onReload}>
              Check again
            </button>
          </dd>
        </div>
      </dl>
      {cert && status.methods.some((m) => m.acs !== "") ? (
        <div class={cert.warn ? "banner banner-warning" : "banner banner-info"} role="status">
          <p>{cert.text} It is not renewed automatically; ask AllCode support before it expires.</p>
        </div>
      ) : null}
      <section class="idn-group" aria-labelledby="idn-methods">
        <h2 id="idn-methods" class="idn-group-head">
          Methods
        </h2>
        {status.methods.length === 0 ? (
          <p class="idn-quiet">
            No sign-in method is enabled, so no one can sign in. Add one in the <a href={EDITOR_HREF}>settings below</a>.
          </p>
        ) : (
          <ul class="idn-methods">
            {status.methods.map((method) => (
              <Method key={method.id} method={method} source={source} />
            ))}
          </ul>
        )}
      </section>
      {status.trustedTokens.length > 0 ? (
        <section class="idn-group" aria-labelledby="idn-tokens">
          <h2 id="idn-tokens" class="idn-group-head">
            Trusted tokens
          </h2>
          <p class="idn-quiet">Tools that bring their own token. They never show on the sign-in page.</p>
          <ul class="idn-methods">
            {status.trustedTokens.map((token) => (
              <li key={token.id} class="idn-method">
                <h3 class="idn-method-title">{token.label || token.issuer}</h3>
                <dl class="idn-urls">
                  <Url term="Issuer" value={token.issuer} />
                  <Url term="Key set" value={token.jwksUri} />
                  <div>
                    <dt>Mode</dt>
                    <dd>{token.mode}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function Url({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>
        <code class="idn-url">{value}</code>
      </dd>
    </div>
  );
}

type Confirm = { phase: "idle" } | { phase: "busy" } | { phase: "done"; at: string } | { phase: "failed"; failure: Failure };

function Method({ method, source }: { method: MethodStatus; source: SignInSource }) {
  const [confirm, setConfirm] = useState<Confirm>({ phase: "idle" });
  const title = method.label || KIND_LABELS[method.kind] || method.kind;
  const confirmIt = () => {
    setConfirm({ phase: "busy" });
    void source.confirm(method.id).then((result) => setConfirm(result.ok ? { phase: "done", at: result.value } : { phase: "failed", failure: result.failure }));
  };
  return (
    <li class="idn-method">
      <div class="idn-method-line">
        <h3 class="idn-method-title">{title}</h3>
        <span class={method.working ? "chip chip-green" : "chip chip-red"}>{method.working ? "Working" : "Not working"}</span>
      </div>
      <p class="idn-sub">
        {KIND_LABELS[method.kind] ?? method.kind}
        {method.type ? ` · ${method.type.toUpperCase()}` : ""}
        {!method.working && method.reason ? ` · ${method.reason}` : ""}
      </p>
      {method.acs || method.redirectUri ? (
        <dl class="idn-urls">
          {method.entityId ? <Url term="Entity id" value={method.entityId} /> : null}
          {method.acs ? <Url term="ACS URL" value={method.acs} /> : null}
          {method.redirectUri ? <Url term="Redirect URI" value={method.redirectUri} /> : null}
        </dl>
      ) : null}
      <div class="idn-actions">
        {method.acs ? (
          <a class="button" href={source.metadataHref(method.id)} download>
            Download SP metadata
          </a>
        ) : null}
        {method.kind === "sso" ? (
          <button type="button" class="button" disabled={confirm.phase === "busy"} onClick={confirmIt}>
            {confirm.phase === "busy" ? "Confirming…" : "Confirm test sign-in"}
          </button>
        ) : null}
      </div>
      {method.kind === "sso" ? (
        <p class="idn-sub">
          Sign in through it once on <a href={TEST_SIGN_IN_PATH}>this proxy's sign-in page</a> in a private window, then confirm here.
        </p>
      ) : null}
      {confirm.phase === "done" ? (
        <p class="idn-ok" role="status">
          Test sign-in confirmed{confirm.at ? ` at ${formatWhen(confirm.at)}` : ""}.
        </p>
      ) : null}
      {confirm.phase === "failed" ? (
        <p class="field-error" role="alert">
          {failureMessage(confirm.failure, "customer")}
        </p>
      ) : null}
      {method.takesSecret ? <SecretForm method={method} source={source} /> : null}
    </li>
  );
}

type Write = { phase: "idle" } | { phase: "open" } | { phase: "busy" } | { phase: "saved" } | { phase: "failed"; failure: Failure };

/** Write-only: the field is a password input, emptied on every outcome, and nothing reads it back. */
function SecretForm({ method, source }: { method: MethodStatus; source: SignInSource }) {
  const [write, setWrite] = useState<Write>({ phase: "idle" });
  const [value, setValue] = useState("");
  const id = `idn-secret-${method.id.replace(/[^A-Za-z0-9]/g, "")}`;
  if (write.phase === "idle" || write.phase === "saved") {
    return (
      <div class="idn-secret">
        {write.phase === "saved" ? (
          <p class="idn-ok" role="status">
            Saved. The secret is in this proxy's AWS account and is never shown again.
          </p>
        ) : null}
        <button type="button" class="button" onClick={() => setWrite({ phase: "open" })}>
          {write.phase === "saved" ? "Replace client secret" : "Set client secret"}
        </button>
      </div>
    );
  }
  const submit = (event: Event) => {
    event.preventDefault();
    if (value === "" || write.phase === "busy") return;
    const sent = value;
    setValue("");
    setWrite({ phase: "busy" });
    void source.putSecret(method.id, sent).then((result) => setWrite(result.ok ? { phase: "saved" } : { phase: "failed", failure: result.failure }));
  };
  return (
    <form class="idn-secret" onSubmit={submit} autocomplete="off">
      <div class="field">
        {/* help-exempt: shared with web/home, which has no help topics; the label says it all. */}
        <label for={id}>Client secret for {method.label || method.id}</label>
        <input
          id={id}
          type="password"
          class="input"
          value={value}
          autocomplete="off"
          spellcheck={false}
          aria-describedby={`${id}-hint`}
          onInput={(event) => setValue((event.currentTarget as HTMLInputElement).value)}
        />
        <p id={`${id}-hint`} class="field-hint">
          Paste it from your identity provider. It is written to this proxy's AWS account and cannot be read back here.
        </p>
      </div>
      {write.phase === "failed" ? (
        <p class="field-error" role="alert">
          Not saved. {failureMessage(write.failure, "customer")}
        </p>
      ) : null}
      <div class="idn-actions">
        <button type="submit" class="button button-primary" disabled={value === "" || write.phase === "busy"}>
          {write.phase === "busy" ? "Saving…" : "Save secret"}
        </button>
        <button
          type="button"
          class="button"
          onClick={() => {
            setValue("");
            setWrite({ phase: "idle" });
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
